import React, { useState } from 'react';
import { collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { ShieldCheck } from 'lucide-react';

// Promote another coach by their sign-in email. They must have signed up once
// (users doc exists); firestore.rules lets coaches update user roles.
export const StaffCard: React.FC = () => {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const promote = async () => {
    const target = email.trim().toLowerCase();
    if (!target) return;
    setBusy(true);
    setMessage(null);
    try {
      const snap = await getDocs(query(collection(db, 'users'), where('email', '==', target)));
      if (snap.empty) {
        setMessage({
          ok: false,
          text: 'No account with that email yet. Have them sign up on the site first, then promote them here.',
        });
      } else {
        await updateDoc(doc(db, 'users', snap.docs[0].id), { role: 'coach' });
        setMessage({ ok: true, text: `${target} is now a coach.` });
        setEmail('');
      }
    } catch (e) {
      console.error('Promote failed:', e);
      setMessage({ ok: false, text: 'Could not update that account. Check the console for details.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-tiger p-6">
      <h3 className="text-lg font-bold text-tiger-primary-black flex items-center gap-2 mb-1">
        <ShieldCheck className="w-5 h-5" /> Coaching Staff
      </h3>
      <p className="text-sm text-tiger-neutral-500 mb-4">
        Give another coach full admin access. They sign up on the site first, then you promote their email.
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="assistant.coach@example.com"
          className="flex-1 px-4 py-3 border border-tiger-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tiger-primary-black focus:border-transparent transition"
        />
        <button
          onClick={promote}
          disabled={busy || !email.trim()}
          className="px-6 py-3 bg-tiger-primary-black text-white font-bold rounded-lg hover:bg-tiger-neutral-800 transition-colors disabled:opacity-50"
        >
          {busy ? 'Promoting…' : 'Make Coach'}
        </button>
      </div>
      {message && (
        <p className={`mt-3 text-sm font-bold ${message.ok ? 'text-green-700' : 'text-red-600'}`}>{message.text}</p>
      )}
    </div>
  );
};
